import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect, useLocation, useParams } from 'react-router-dom';

import { Box } from '@mui/material';
import { LOANS_ROUTING_PARAMS, LoansPaths } from 'app/configs/routesConfig/loans';
import { PENDING_CHANGES_SUBTAB } from 'app/pages/loanApplication/LoanHeaders/RedesignedLoanInfoHeader/PendingChangesDrawer/PendingChangesDrawer.constants';
import PageBlock from 'app/shared/componentsRestyled/common/PageBlock';
import SearchInputCDS from 'app/shared/componentsRestyled/coreDesignSystem/SearchInputCDS';
import { TabsCDS } from 'app/shared/componentsRestyled/coreDesignSystem/TabsCDS';
import { TabsStyle } from 'app/shared/componentsRestyled/coreDesignSystem/TabsCDS/TabsCDS.constants';

import {
  LoanApplicationTabRouteParam,
  TasksSubtab,
  TasksSubtabRouteParam,
} from 'constants/loanApplication';
import { TabStatusRelation } from 'constants/tasks';
import { useBreakpoints } from 'hooks/useBreakpoints';
import useSearch from 'hooks/useSearch';
import useTableFilter from 'hooks/useTableFilter';

import { setActiveTabWithNavigationAction } from 'store/loanApplications/actions';

import Tasks from './Tasks';
import { TasksTestIds } from './Tasks.testIds';
import { getToolbarStyles } from './TasksPage.styles';

const pageBlockClasses = {
  content: '!gap-0',
};

const tabsDefs = Object.values(TasksSubtab).map(({ tabName, tabRoute }) => ({
  label: tabName,
  value: tabRoute,
}));

const TasksPage = () => {
  const { setSearchText } = useSearch();
  const { isMobile } = useBreakpoints();
  const { columnFilters, onColumnFiltersChange, applyFilters } = useTableFilter();

  const { state: navigationState } = useLocation();
  const { applicationId, subtab } = useParams();
  const dispatch = useDispatch();

  const onChangeTab = useCallback(
    (_event, value) => {
      dispatch(
        setActiveTabWithNavigationAction({
          activeTab: LoanApplicationTabRouteParam.TASKS,
          activeSubtab: value,
          applicationId,
        }),
      );
    },
    [applicationId, dispatch],
  );

  const status = useMemo(() => TabStatusRelation[subtab], [subtab]);
  const toolbarStyles = useMemo(() => getToolbarStyles({ isMobile }), [isMobile]);

  const isPendingChangesDrawerOpen = subtab === PENDING_CHANGES_SUBTAB;
  const isRedirectNeeded =
    !isPendingChangesDrawerOpen && !Object.values(TasksSubtabRouteParam).includes(subtab);

  if (isRedirectNeeded) {
    const applicationPathname = LoansPaths.applicationManagement.generatePath({
      [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
      [LOANS_ROUTING_PARAMS.tab]: LoanApplicationTabRouteParam.TASKS,
      [LOANS_ROUTING_PARAMS.subtab]: TasksSubtabRouteParam.PENDING,
    });

    // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
    const to = {
      pathname: applicationPathname,
      state: navigationState,
    };

    return <Redirect to={to} />;
  }

  return (
    <PageBlock isFullHeight withDivider classes={pageBlockClasses} title="Tasks">
      <Box sx={toolbarStyles}>
        <TabsCDS
          allowScrollButtonsMobile={isMobile}
          data-testid={TasksTestIds.TABS}
          tabs={tabsDefs}
          tabsStyle={TabsStyle.ON_CARD}
          value={subtab}
          variant={isMobile ? 'scrollable' : 'standard'}
          onChange={onChangeTab}
        />

        <SearchInputCDS
          focusOnType
          searchOnType
          ariaLabel="Search tasks"
          width={isMobile ? '100%' : '280px'}
          onSearch={setSearchText}
        />
      </Box>

      <Tasks
        applyFilters={applyFilters}
        columnFilters={columnFilters}
        status={status}
        onColumnFiltersChange={onColumnFiltersChange}
      />
    </PageBlock>
  );
};

export default TasksPage;
